// components/layout/services-dropdown.jsx
'use client';

import { useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import { serviceCategories, servicesPromo } from '@/content/navigation';
import DesktopServicesPanel from '@/components/layout/services-panel-desktop';
import MobileServicesPanel from '@/components/layout/services-panel-mobile';

export default function ServicesDropdown({
    isMobile = false,
    onLinkClick,
    megaMenuClassName = '',
    variant = 'dark',
}) {
    const pathname = usePathname();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState(0);

    const isLight = variant === 'light';

    // Close dropdown on route change
    useEffect(() => setDropdownOpen(false), [pathname]);

    const styles = useMemo(() => {
        const textColor = isLight ? 'text-white' : 'text-white';
        const hoverTextColor = isLight ? 'hover:text-white/80' : 'hover:text-white/80';

        const panelBg = isLight
            ? 'bg-white border border-slate-200 text-slate-800'
            : 'bg-slate-950/95 border border-slate-800 text-white';

        return { textColor, hoverTextColor, panelBg };
    }, [isLight]);

    return (
        <div
            className="relative py-0 max-sm:py-0 max-sm:px-0 px-4"
            onMouseEnter={() => !isMobile && setDropdownOpen(true)}
            onMouseLeave={() => !isMobile && setDropdownOpen(false)}
        >
            <button
                type="button"
                onClick={() => (isMobile ? setDropdownOpen((v) => !v) : null)}
                className={`flex items-center cursor-pointer px-0 gap-1 max-sm:w-full ml-[-5px] -mr-4 hover:underline transition lg:py-8 ${styles.textColor} ${styles.hoverTextColor}`}
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
            >
                Services
                <ChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {dropdownOpen && (
                <div
                    data-lenis-prevent-wheel
                    className={`${
                        isMobile
                            ? `mt-2 rounded-2xl p-4 flex flex-col space-y-4 ${styles.panelBg}`
                            : `fixed right-0 mx-auto left-0 top-0 mt-0 z-999 w-[1080px] max-w-[calc(100vw-40px)] rounded-2xl p-4 shadow-2xl backdrop-blur-xl ${styles.panelBg} ${megaMenuClassName}`
                    }`}
                >
                    {isMobile ? (
                        <MobileServicesPanel
                            isLight={isLight}
                            categories={serviceCategories}
                            activeCategory={activeCategory}
                            setActiveCategory={setActiveCategory}
                            onLinkClick={onLinkClick}
                            promo={servicesPromo}
                            closeDropdown={() => setDropdownOpen(false)}
                        />
                    ) : (
                        <DesktopServicesPanel
                            isLight={isLight}
                            categories={serviceCategories}
                            activeCategory={activeCategory}
                            setActiveCategory={setActiveCategory}
                            onLinkClick={onLinkClick}
                            promo={servicesPromo}
                            closeDropdown={() => setDropdownOpen(false)}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
